import { useState } from 'react';

import { IconCopy, IconRefresh } from '@tabler/icons-react';
import { motion } from 'motion/react';
import toast from 'react-hot-toast';

import { cn } from '@/utils/tailwind';

import { useRequiredSmartSearchChatContext } from '../../SmartSearchChatContext';
import { PathwayUIMessage } from '../../types';
import ActionIcon from '../ActionIcon';
import Time from '../Time';

interface UserMessageProps {
  message: PathwayUIMessage;
}

const getMessageText = (message: PathwayUIMessage): string =>
  message.parts
    .filter(p => p.type === 'text')
    .map(p => p.text)
    .join('\n');

const getMessageCreatedAt = (message: PathwayUIMessage): string => {
  if (message.metadata?.createdAt) {
    return new Date(message.metadata.createdAt).toISOString();
  }
  return new Date().toISOString();
};

const UserMessage: React.FC<UserMessageProps> = ({ message }) => {
  const [hover, setHover] = useState(false);
  const chat = useRequiredSmartSearchChatContext();

  const text = getMessageText(message);
  const isStreaming =
    chat.status === 'streaming' || chat.status === 'submitted';

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleRetry = () => {
    if (isStreaming) return;
    const index = chat.messages.findIndex(m => m.id === message.id);
    if (index === -1) return;
    chat.setMessages(msgs => msgs.slice(0, index));
    chat.sendMessage({ text });
  };

  return (
    <motion.div
      className="flex items-center justify-end"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="flex max-w-3/4 flex-col items-end gap-2">
        <p className="text-variant-body1 bg-grayscale-gray2/30 rounded-lg px-4 py-2">
          {text}
        </p>
        <div
          className={cn('flex items-center opacity-0 transition-opacity', {
            'opacity-100': hover,
          })}
        >
          <Time timestamp={getMessageCreatedAt(message)} className="mr-3" />
          <ActionIcon
            icon={IconRefresh}
            tooltip="Retry"
            onClick={handleRetry}
            disabled={isStreaming}
          />
          <ActionIcon icon={IconCopy} tooltip="Copy" onClick={handleCopy} />
        </div>
      </div>
    </motion.div>
  );
};

export default UserMessage;
